'use client';

import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';
import type { PromptBarMode } from './usePromptBarMode';
import { type ActionItem, usePromptBarActions } from './usePromptBarActions';

interface MissionChipsRowProps {
  locale: Locale;
  mode: PromptBarMode;
  isAuthenticated: boolean;
  className?: string;
  /**
   * Render every chip unselected, ignoring the URL-derived current mission. Landing surfaces
   * (salaries, my-bestie, account settings) show the chips as plain navigation, so none should
   * carry the selected (bold) state — useCurrentMission falls back to `jobs` for those paths.
   */
  forceUnselected?: boolean;
}

const chipClassName = (selected: boolean): string =>
  cn(
    'whitespace-nowrap rounded-full border border-input bg-surface px-2 py-1 text-xs text-ink shadow-sm transition-colors sm:px-2.5 lg:text-sm',
    selected ? 'font-semibold' : 'cursor-pointer hover:bg-surface-100',
  );

/**
 * The mission list rendered as a centered row of chips below the prompt bar. The current
 * mission's chip looks identical to the rest but clicking it does nothing (handleClick
 * no-ops on the selected item). Stays on a single row; chip padding/text shrink on narrow
 * viewports to fit, so it never wraps to two rows.
 */
export const MissionChipsRow = ({ locale, mode, isAuthenticated, className, forceUnselected }: MissionChipsRowProps) => {
  const { t } = useTranslation('common');
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, mode, isAuthenticated);
  const itemsByKey = new Map([...candidateItems, ...companyItems].map((item) => [item.key, item]));

  // Row order: Coach, Interviews, Jobs, My Jobs, Add job.
  const chips = [
    { item: itemsByKey.get('coaching'), label: t('mc.chip.coach') },
    { item: itemsByKey.get('interviews'), label: t('interviews.menu.title') },
    { item: itemsByKey.get('jobs'), label: t('mc.chip.jobs') },
    { item: itemsByKey.get('candidate-management'), label: t('mc.chip.my.jobs') },
    { item: itemsByKey.get('add-job'), label: t('mc.chip.add') },
  ].filter((entry): entry is { item: ActionItem; label: string } => Boolean(entry.item));

  const renderNavChip = ({ item, label }: { item: ActionItem; label: string }, extra?: string) => {
    const selected = forceUnselected ? false : item.selected;
    return (
      <a
        key={item.key}
        href={item.href}
        aria-current={selected ? 'page' : undefined}
        onClick={(event) => {
          // Let the browser handle modifier / middle clicks (open in new tab); intercept
          // plain left-clicks for soft-navigation through handleClick.
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          // Pass the display selection so a force-unselected chip still navigates (handleClick
          // no-ops on a `selected` item, and the URL fallback marks `jobs` selected here).
          handleClick({ ...item, selected });
        }}
        className={cn(chipClassName(selected), extra)}
      >
        {label}
      </a>
    );
  };

  return (
    <div className={cn('flex w-full flex-nowrap items-center justify-center gap-1 sm:gap-1.5', className)}>
      {chips.map((entry) => renderNavChip(entry, entry.item.key === 'jobs' ? 'uppercase' : undefined))}
    </div>
  );
};
